import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Field, Control, Input } from "rbx";
import moment from "moment";
import { useDebouncedValue } from "../../../hooks";
import { omit, getFilterState, saveFilterState } from "../../../utils";
import DataTable from "../../../components/DataTable";
import PageHeader from "../../../components/PageHeader";

import { ORDER_VIEW_COLUMNS } from "./columns";
import PrintBOL from "../../../components/PrintBOL";

import { ALL_ORDERS_VIEW_BY_PAGE_QUERY } from "../../../graphql/Orders/Orders.graphql";

import OrderFilters from "./OrderFilters";

import { useSystemCodes } from "../../../utils/hooks";

import OrderStatusFilter from "./OrderStatusFilter";

import Loading from "../../../components/Loading";

const DEFAULT_FILTERS = {};

function OrdersPage({ defaultFilters = DEFAULT_FILTERS }) {
  const { data: systemCodes } = useSystemCodes({
    Category: "Order",
    CodeName: "OrderStatus"
  });
  const history = useHistory();
  const INITIAL_FILTER_STATE = getFilterState("ORDER_FILTERS", {
    ...defaultFilters
  });
  const [codes, setCodes] = useState(null);

  const [filters, setFilters] = useState({ ...INITIAL_FILTER_STATE });

  const [orderNumber, setOrderNumber] = useState("");

  const debouncedOrderNumber = useDebouncedValue(orderNumber, 500);

  useEffect(() => {
    if (Array.isArray(systemCodes) && !codes) {
      setCodes(
        systemCodes.map(({ RecId, CodeDescription }) => ({
          value: RecId,
          label: CodeDescription
        }))
      );
      if (
        !Array.isArray(filters.StatusRecIdRef_in) ||
        (Array.isArray(filters.StatusRecIdRef_in) &&
          !filters.StatusRecIdRef_in.length)
      ) {
        const newFilters = systemCodes.filter(s =>
          ["U", "DA", "SCH", "VEH", "BK-E", "EXP", "IMP", "RV"].includes(
            s.CodeId
          )
        );
        setFilters(prev => ({
          ...prev,
          StatusRecIdRef_in: newFilters.map(f => f.RecId)
        }));
      }
    }
  }, [systemCodes, filters, codes]);

  useEffect(() => {
    saveFilterState("ORDER_FILTERS", filters);
  }, [filters]);

  const handleSetFilters = inputs => {
    saveFilterState("ORDER_FILTERS", inputs);
    setFilters({ ...inputs });
  };

  useEffect(() => {
    saveFilterState("ORDER_FILTERS", filters);
  }, [filters]);

  useEffect(() => {
    setFilters(prev =>
      debouncedOrderNumber
        ? { ...prev, OrderNumber: parseInt(debouncedOrderNumber, 10) }
        : omit(prev, ["OrderNumber"])
    );
  }, [debouncedOrderNumber]);

  const resetFilters = () => {
    const newFilters = systemCodes.filter(s =>
      ["U", "DA", "SCH", "VEH", "BK-E", "EXP", "IMP", "RV"].includes(s.CodeId)
    );
    const inputs = {
      ...DEFAULT_FILTERS,
      StatusRecIdRef_in: newFilters.map(f => f.RecId)
    };
    handleSetFilters({
      ...inputs
    });
    saveFilterState("ORDER_FILTERS", inputs);
  };

  // this enables us to use to / from === same day but still query a range
  const parseFilters = input => {
    const { StatusRecIdRef_in, ...where } = { ...input };
    if (input.PickupDate_gt) {
      where.PickupDate_gt = moment(input.PickupDate_gt)
        .startOf("day")
        .toISOString();
    }
    if (input.PickupDate_lt) {
      where.PickupDate_lt = moment(input.PickupDate_lt)
        .endOf("day")
        .toISOString();
    }
    if (input.OrderDate_gt) {
      where.OrderDate_gt = moment(input.OrderDate_gt)
        .startOf("day")
        .toISOString();
    }
    if (input.OrderDate_lt) {
      where.OrderDate_lt = moment(input.OrderDate_lt)
        .endOf("day")
        .toISOString();
    }
    if (Array.isArray(StatusRecIdRef_in) && StatusRecIdRef_in.length) {
      where.StatusRecIdRef_in = StatusRecIdRef_in;
    }
    return where;
  };

  if (!Array.isArray(filters.StatusRecIdRef_in) || !codes) {
    return <Loading />;
  }
  return (
    <React.Fragment>
      <PageHeader
        FilterComponent={OrderFilters}
        columns={ORDER_VIEW_COLUMNS}
        filters={filters}
        name="VW_OrdersByPage"
        query={ALL_ORDERS_VIEW_BY_PAGE_QUERY}
        resetFilters={resetFilters}
        setFilters={handleSetFilters}
        title="Orders"
        onAddClick={() => {
          history.push("/dispatch/orders/new");
        }}
      >
        <Control>
          <PrintBOL filters={filters} />
        </Control>
      </PageHeader>
      <Field kind="group">
        <Control>
          <Input
            placeholder="Order Number"
            style={{ height: 38 }}
            type="text"
            value={orderNumber}
            onChange={e => {
              setOrderNumber(
                /^(\s*|\d+)$/.test(e.target.value)
                  ? e.target.value
                  : orderNumber
              );
            }}
          />
        </Control>
        <Control expanded>
          <OrderStatusFilter
            filters={filters}
            handleSetFilters={handleSetFilters}
            options={codes}
          />
        </Control>
      </Field>
      <DataTable
        isPaginated
        columns={ORDER_VIEW_COLUMNS}
        dataQuery={ALL_ORDERS_VIEW_BY_PAGE_QUERY}
        hiddenColumns={["StatusIdListOrder"]}
        initialSortBy={[
          { id: "StatusIdListOrder", desc: false },
          {
            id: "PickupDate",
            desc: false
          },
          { id: "OrderNumber", desc: false }
        ]}
        name="VW_OrdersByPage"
        where={parseFilters(filters)}
        onRowClick={({ RecId }) =>
          history.push(`/dispatch/orders/${RecId}/summary`)
        }
      />
    </React.Fragment>
  );
}
OrdersPage.propTypes = {
  defaultFilters: PropTypes.object
};
export default OrdersPage;
